import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigation } from '../hooks/useNavigation';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Plus,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  CheckCircle,
  Check,
  AlertCircle,
  Printer,
  AlertTriangle,
  Info,
  Loader2,
  Tag,
  Box,
  Package2,
  Layers,
  Warehouse,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Minus,
  ArrowUp,
  ArrowDown,
  Calculator
} from 'lucide-react';
import toast from 'react-hot-toast';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Quantity formatting utility function
const formatQuantity = (quantity) => {
  const num = parseFloat(quantity || 0);
  if (num >= 0 && num <= 9 && num % 1 === 0) {
    return num.toString(); // Show single digit for 0-9 whole numbers
  }
  // For decimals, always show 2 decimal places
  if (num % 1 !== 0) {
    return num.toFixed(2);
  }
  // For whole numbers > 9, show without decimals
  return num.toString();
};

const StockManagement = () => {
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { formatCurrency } = useSettings();
  const { navigationItems } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Stock state management
  const [loading, setLoading] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [stockStats, setStockStats] = useState({});
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 0,
    to: 0,
    has_more_pages: false
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    search: '',
    sort_by: 'stock_level',
    sort_order: 'asc'
  });

  // Stock adjustment modal states
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockModalType, setStockModalType] = useState(''); // 'in', 'out', 'adjust'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockForm, setStockForm] = useState({
    quantity: '',
    unit_of_measure: 'pcs',
    reason: '',
    cost_price: '',
    selling_price: ''
  });
  const [stockLoading, setStockLoading] = useState(false);
  const [adjustmentReasons, setAdjustmentReasons] = useState({
    supplier_related: [],
    internal: []
  });
  const [selectedReasonCategory, setSelectedReasonCategory] = useState('');

  // Main action modal states
  const [showStockAdjustmentModal, setShowStockAdjustmentModal] = useState(false);
  const [showApproveAdjustmentModal, setShowApproveAdjustmentModal] = useState(false);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [batchIds, setBatchIds] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  
  // Print stock take sheet modal states
  const [showPrintStockTakeModal, setShowPrintStockTakeModal] = useState(false);
  const [stockLevelFilter, setStockLevelFilter] = useState('all'); // 'all', 'positive', 'zero', 'negative'
  const [approvedAdjustments, setApprovedAdjustments] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBatchDetails, setSelectedBatchDetails] = useState(null);
  const [bulkAdjustmentForm, setBulkAdjustmentForm] = useState({
    products: [],
    adjustment_type: 'adjust',
    reason_category: '',
    reason: ''
  });
  const [adjustmentId, setAdjustmentId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);


  // Product search and selection states
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedSearchProduct, setSelectedSearchProduct] = useState(null);
  const [isQuantityFocused, setIsQuantityFocused] = useState(false);
  const [isUomFocused, setIsUomFocused] = useState(false);


  const handleCardClick = useCallback((href) => {
    console.log(`Navigating to: ${href}`);
    navigate(href);
  }, [navigate]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, current_page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      category: 'all',
      status: 'all',
      sortBy: 'name'
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      await fetchStockItems();
      await fetchStockStats();
      toast.success('Stock data refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing stock data:', error);
      toast.error('Failed to refresh stock data');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleExportStock = useCallback(async () => {
    try {
      const response = await apiService.exportStockItems(filters);
      if (response.success) {
        // Create download link
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stock-items-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Stock data exported successfully!');
      } else {
        toast.error('Failed to export stock data');
      }
    } catch (error) {
      console.error('Error exporting stock data:', error);
      toast.error('Failed to export stock data');
    }
  }, [filters]);

  const handleDeleteItem = useCallback(async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteProduct(item.id);
      if (response.success) {
        toast.success('Product deleted successfully!');
        await fetchStockItems();
        await fetchStockStats();
      } else {
        toast.error(response.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setLoading(false);
    }
  }, []);

  // Stock operation handlers
  const handleStockOperation = useCallback((product, operation) => {
    setSelectedProduct(product);
    setStockModalType(operation);
    setStockForm({
      quantity: '',
      unit_of_measure: 'pcs',
      reason: '',
      cost_price: product.unit_cost || '',
      selling_price: product.selling_price_pcs || ''
    });
    setShowStockModal(true);
  }, []);

  const handleCloseStockModal = useCallback(() => {
    setShowStockModal(false);
    setSelectedProduct(null);
    setStockModalType('');
    setSelectedReasonCategory('');
    setStockForm({
      quantity: '',
      unit_of_measure: 'pcs',
      reason: '',
      cost_price: '',
      selling_price: ''
    });
  }, []);

  const handleStockFormChange = useCallback((field, value) => {
    setStockForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Generate adjustment ID
  const generateAdjustmentId = useCallback(async () => {
    try {
      // Try to get the next adjustment ID from the API
      const data = await apiService.getLastAdjustmentId();
      const nextId = data.data?.next_id || `BULK-${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}001`;
      setAdjustmentId(nextId);
    } catch (error) {
      console.error('Error generating adjustment ID:', error);
      // Fallback: generate ID based on current date
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const fallbackId = `BULK-${year}${month}${day}001`;
      setAdjustmentId(fallbackId);
    }
  }, []);

  // Main action button handlers
  const handleStockAdjustmentClick = useCallback(async () => {
    setBulkAdjustmentForm({
      products: [],
      adjustment_type: 'adjust',
      reason_category: '',
      reason: ''
    });
    // Reset search states
    setSearchQuery('');
    setSearchResults([]);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    setSelectedSearchProduct(null);
    setIsQuantityFocused(false);
    setIsUomFocused(false);
    
    setShowStockAdjustmentModal(true);
    await generateAdjustmentId();
  }, [generateAdjustmentId]);

  const handleApproveAdjustmentClick = useCallback(async () => {
    try {
      setStockLoading(true);
      
      // Fetch pending adjustments first
      try {
        const pendingResponse = await apiService.getPendingAdjustments();
        if (pendingResponse.success) {
          setPendingAdjustments(pendingResponse.data);
          setBatchIds(pendingResponse.batch_ids || []);
        } else {
          toast.error('Failed to load pending adjustments');
        }
      } catch (error) {
        console.error('❌ Error fetching pending adjustments:', error);
        toast.error('Failed to load pending adjustments');
      }
      
      // Fetch approved adjustments
      try {
        const approvedResponse = await apiService.getApprovedAdjustments();
      if (approvedResponse.success) {
        setApprovedAdjustments(approvedResponse.data || []);
      } else {
        toast.error('Failed to load approved adjustments');
      }
      } catch (error) {
        console.error('❌ Error fetching approved adjustments:', error);
        toast.error('Failed to load approved adjustments');
      }
      
      setShowApproveAdjustmentModal(true);
    } catch (error) {
      console.error('❌ General error in handleApproveAdjustmentClick:', error);
      toast.error('Failed to load adjustments');
    } finally {
      setStockLoading(false);
    }
  }, []);

  const handlePrintStockTakeSheet = useCallback(() => {
    // Open modal instead of printing directly
    setShowPrintStockTakeModal(true);
  }, []);

  const getFilteredStockItems = useMemo(() => {
    switch (stockLevelFilter) {
      case 'positive':
        return stockItems.filter(item => (item.current_stock || 0) > 0);
      case 'zero':
        return stockItems.filter(item => (item.current_stock || 0) === 0);
      case 'negative':
        return stockItems.filter(item => (item.current_stock || 0) < 0);
      default:
        return stockItems;
    }
  }, [stockItems, stockLevelFilter]);

  const handlePrintFromModal = useCallback(() => {
    const itemsToPrint = getFilteredStockItems;
    
    if (itemsToPrint.length === 0) {
      toast.error('No items to print with the selected filter');
      return;
    }

    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const filterLabel = stockLevelFilter === 'all' ? 'All Items' : 
                       stockLevelFilter === 'positive' ? 'Positive Stock Only' :
                       stockLevelFilter === 'zero' ? 'Zero Stock Only' : 'Negative Stock Only';

    // Create a hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    document.body.appendChild(printFrame);

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Stock Take Sheet - ${currentDate}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #1e40af;
              margin-bottom: 5px;
              font-size: 24px;
            }
            .date {
              text-align: center;
              color: #666;
              margin-bottom: 5px;
              font-size: 14px;
            }
            .filter-info {
              text-align: center;
              color: #7c3aed;
              font-weight: bold;
              margin-bottom: 20px;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #333;
              padding: 8px;
              text-align: left;
              font-size: 12px;
            }
            th {
              background-color: #1e40af;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9fafb;
            }
            .count-column {
              width: 100px;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #666;
              font-size: 11px;
            }
            @page {
              margin: 15mm;
            }
          </style>
        </head>
        <body>
          <h1>Stock Take Sheet</h1>
          <div class="date">${currentDate}</div>
          <div class="filter-info">${filterLabel} (${itemsToPrint.length} items)</div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Product Name</th>
                <th style="width: 120px;">Category</th>
                <th style="width: 60px;">UOM</th>
                <th style="width: 80px;">System Stock</th>
                <th class="count-column">Physical Count</th>
                <th class="count-column">Variance</th>
              </tr>
            </thead>
            <tbody>
              ${itemsToPrint.map((item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.name || 'N/A'}</td>
                  <td>${item.category || 'N/A'}</td>
                  <td>${item.unit_of_measure || 'pcs'}</td>
                  <td>${formatQuantity(item.current_stock || 0)}</td>
                  <td class="count-column">_____________</td>
                  <td class="count-column">_____________</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p style="margin-bottom: 5px;">Counted by: _____________________ &nbsp;&nbsp; Date: _____________________ &nbsp;&nbsp; Signature: _____________________</p>
            <p>Verified by: _____________________ &nbsp;&nbsp; Date: _____________________ &nbsp;&nbsp; Signature: _____________________</p>
          </div>
        </body>
      </html>
    `;

    const doc = printFrame.contentWindow.document;
    doc.open();
    doc.write(printContent);
    doc.close();

    // Wait for content to load then print
    printFrame.contentWindow.onload = () => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
      
      // Remove iframe after printing
      setTimeout(() => {
        document.body.removeChild(printFrame);
      }, 1000);
    };
    
    toast.success('Stock take sheet sent to printer', {
      icon: '🖨️',
      duration: 2000,
    });
    
    // Close modal after printing
    setShowPrintStockTakeModal(false);
  }, [getFilteredStockItems, stockLevelFilter]);

  const handleCloseStockAdjustmentModal = useCallback(() => {
    setShowStockAdjustmentModal(false);
    setBulkAdjustmentForm({
      products: [],
      adjustment_type: 'adjust',
      reason_category: '',
      reason: '',
      notes: ''
    });
  }, []);

  const handleCloseApproveAdjustmentModal = useCallback(() => {
    setShowApproveAdjustmentModal(false);
    setPendingAdjustments([]);
    setBatchIds([]);
    setSelectedBatchId('');
    setSelectedAction('');
    // Don't clear approvedAdjustments - keep them for next time modal opens
  }, []);

  const handleViewDetails = useCallback((batch) => {
    setSelectedBatchDetails(batch);
    setShowDetailsModal(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedBatchDetails(null);
  }, []);

  const handleApproveIndividualAdjustment = useCallback(async (adjustmentId, action) => {
    try {
      setStockLoading(true);
      const response = await apiService.approveAdjustment(adjustmentId, action);
      
      if (response.success) {
        toast.success(response.message, {
          duration: 4000,
          style: {
            background: '#10b981',
            color: 'white',
          },
        });
        
        // Remove the processed adjustment from the list
        setPendingAdjustments(prev => prev.filter(adj => adj.id !== adjustmentId));
        
        // Refresh stock data
        setTimeout(() => {
          fetchStockData();
        }, 100);
      } else {
        toast.error(response.message || 'Failed to process adjustment');
      }
    } catch (error) {
      console.error('Error processing adjustment:', error);
      toast.error('Failed to process adjustment. Please try again.');
    } finally {
      setStockLoading(false);
    }
  }, []);

  const handleBulkApproveAdjustments = useCallback(async (action) => {
    if (pendingAdjustments.length === 0) {
      toast.error('No adjustments to process');
      return;
    }

    try {
      setStockLoading(true);
      const adjustmentIds = pendingAdjustments.map(adj => adj.id);
      const response = await apiService.bulkApproveAdjustments(adjustmentIds, action);
      
      if (response.success) {
        toast.success(response.message, {
          duration: 4000,
          style: {
            background: '#10b981',
            color: 'white',
          },
        });
        
        // Clear all adjustments from the list
        setPendingAdjustments([]);
        
        // Refresh stock data
        setTimeout(() => {
          fetchStockData();
        }, 100);
      } else {
        toast.error(response.message || 'Failed to process adjustments');
      }
    } catch (error) {
      console.error('Error processing bulk adjustments:', error);
      toast.error('Failed to process adjustments. Please try again.');
    } finally {
      setStockLoading(false);
    }
  }, [pendingAdjustments]);

  const handleProcessSelectedAdjustment = useCallback(async () => {
    if (!selectedBatchId) {
      toast.error('Please select an adjustment ID to process');
      return;
    }
    
    if (!selectedAction) {
      toast.error('Please select an action (Approve or Reject)');
      return;
    }

    try {
      setStockLoading(true);
      // Get all adjustment IDs for the selected batch
      const batchAdjustmentIds = pendingAdjustments
        .filter(adj => adj.batch_id === selectedBatchId)
        .map(adj => adj.id);
      
      if (batchAdjustmentIds.length === 0) {
        toast.error('No adjustments found for the selected batch');
        return;
      }

      const response = await apiService.bulkApproveAdjustments(batchAdjustmentIds, selectedAction);
      
      if (response.success) {
        toast.success(response.message, {
          duration: 4000,
          style: {
            background: '#10b981',
            color: 'white',
          },
        });
        
        
        // Remove the processed adjustments from the pending list
        setPendingAdjustments(prev => prev.filter(adj => adj.batch_id !== selectedBatchId));
        setBatchIds(prev => prev.filter(batch => batch.batch_id !== selectedBatchId));
        setSelectedBatchId('');
        
        // Refresh stock data and approved adjustments
        setTimeout(() => {
          fetchStockData();
          fetchApprovedAdjustments();
        }, 100);
      } else {
        toast.error(response.message || 'Failed to process adjustment batch');
      }
    } catch (error) {
      console.error('Error processing selected adjustment batch:', error);
      toast.error('Failed to process adjustment batch. Please try again.');
    } finally {
      setStockLoading(false);
    }
  }, [selectedBatchId, selectedAction, pendingAdjustments]);

  const handleBulkAdjustmentFormChange = useCallback((field, value) => {
    setBulkAdjustmentForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // Save bulk adjustment (pending approval)
  const handleProcessAdjustment = useCallback(async () => {
    if (bulkAdjustmentForm.products.length === 0 || !bulkAdjustmentForm.reason) {
      toast.error('Please add products and select a reason before saving');
      return;
    }

    // Validate that all products have valid quantities (including 0)
    const invalidProducts = bulkAdjustmentForm.products.filter(product => {
      const totalQuantity = product.total_quantity;
      return totalQuantity === undefined || totalQuantity === null || totalQuantity < 0;
    });

    if (invalidProducts.length > 0) {
      toast.error('Please enter valid quantities for all products before saving');
      return;
    }

    try {
      setStockLoading(true);
      
      // Prepare bulk adjustment data
      const bulkAdjustmentData = {
        adjustment_type: bulkAdjustmentForm.adjustment_type,
        reason: bulkAdjustmentForm.reason,
        products: bulkAdjustmentForm.products.map(product => {
          // Send the total quantity directly (already in the correct unit)
          return {
            product_id: product.id,
            quantity: product.total_quantity, // Send total quantity directly
            unit_of_measure: 'pcs', // Send as pieces (base unit)
            new_pcs: product.raw_pcs_quantity || 0,
            new_ctn: product.raw_ctn_quantity || 0,
            cost_price: product.unit_cost,
            selling_price: product.selling_price
          };
        })
      };

      // Debug: Log the data being sent

      // Send bulk adjustment to API
      await apiService.bulkAdjustStock(bulkAdjustmentData);
      
      // Success feedback
      toast.success(`Stock adjustment saved for ${bulkAdjustmentForm.products.length} product${bulkAdjustmentForm.products.length > 1 ? 's' : ''} - pending approval`, {
        duration: 5000,
        style: {
          background: '#10b981',
          color: 'white',
        },
      });

      // Keep modal open and generate next ID
      try {
        await generateAdjustmentId();
      } catch (error) {
        console.error('Failed to generate new ID, using fallback:', error);
      }
      
      // Always use local fallback to ensure ID increments
      const currentId = adjustmentId;
      
      if (currentId && currentId.startsWith('BULK-')) {
        const match = currentId.match(/BULK-(\d{8})(\d{3})/);
        if (match) {
          const datePart = match[1];
          const sequence = parseInt(match[2]);
          const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          
          if (datePart === currentDate) {
            // Same day, increment sequence (handle wrap-around at 999)
            const nextSequence = sequence >= 999 ? '001' : String(sequence + 1).padStart(3, '0');
            const nextId = `BULK-${datePart}${nextSequence}`;
            setAdjustmentId(nextId);
          } else {
            // Different day, start from 001
            const nextId = `BULK-${currentDate}001`;
            setAdjustmentId(nextId);
          }
        }
      } else {
        // No current ID, generate a new one
        const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const nextId = `BULK-${currentDate}001`;
        setAdjustmentId(nextId);
      }
      
      // Reset form data but keep modal open
      setBulkAdjustmentForm({
        adjustment_type: 'adjust',
        reason: '',
        products: []
      });
      
      // Refresh stock data - call directly to avoid dependency issues
      setTimeout(async () => {
        try {
          setLoading(true);
          const [stockResponse, statsResponse, categoriesResponse] = await Promise.all([
            apiService.getStockItems({ page: pagination.current_page, per_page: pagination.per_page, search: filters.search }),
            apiService.getStockStats(),
            apiService.getAllCategories()
          ]);
          
          setStockItems(stockResponse.data || []);
          setStockStats(stockResponse);
          setCategories(categoriesResponse.categories || []);
          setTotalItems(stockResponse.total || 0);
        } catch (error) {
          console.error('Error refreshing stock data:', error);
        } finally {
          setLoading(false);
        }
      }, 100);
      
    } catch (error) {
      console.error('Error saving adjustment:', error);
      
      // Provide more specific error messages based on the error type
      if (error.message && error.message.includes('422')) {
        toast.error('Validation failed. Please check that all products have valid quantities and try again.');
      } else if (error.message && error.message.includes('500')) {
        toast.error('Server error occurred. Please try again later.');
      } else {
        toast.error('Failed to save adjustment. Please try again.');
      }
    } finally {
      setStockLoading(false);
    }
  }, [bulkAdjustmentForm, handleCloseStockAdjustmentModal]);

  // Product search functionality
  const searchProducts = useCallback(async (query) => {
    try {
      setSearching(true);
      console.log('Searching for:', query);
      const data = await apiService.searchProducts(query);
      setSearchResults(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Get all products functionality
  const getAllProducts = useCallback(async () => {
    try {
      setSearching(true);
      const data = await apiService.getAllProducts();
      setSearchResults(data.products || []);
      setShowProductDropdown(true);
    } catch (error) {
      console.error('Error fetching all products:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((query) => {
      if (query.trim().length >= 2) {
        searchProducts(query);
      }
    }, 300),
    [searchProducts]
  );

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    const query = e.target.value.toUpperCase();
    setSearchQuery(query);
    setSelectedIndex(-1);
    
    // Only show dropdown when actively searching (2+ characters)
    if (query.trim().length >= 2) {
      setShowProductDropdown(true);
      debouncedSearch(query);
    } else {
      setShowProductDropdown(false);
      setSearchResults([]);
    }
  }, [debouncedSearch]);

  // Handle product selection
  const handleProductSelect = useCallback((product) => {
    console.log('Selected product:', product);
    setSearchQuery(product.name);
    setSelectedSearchProduct(product);
    setShowProductDropdown(false);
    setSelectedIndex(-1);
    
    // Focus PCS quantity field after selection
    setTimeout(() => {
      const pcsInput = document.getElementById('bulk-quantity-pcs-input');
      if (pcsInput) {
        pcsInput.focus();
        pcsInput.select();
        pcsInput.style.border = '2px solid #3b82f6';
        pcsInput.style.backgroundColor = '#dbeafe';
        pcsInput.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
        
        setTimeout(() => {
          pcsInput.style.border = '';
          pcsInput.style.backgroundColor = '';
          pcsInput.style.boxShadow = '';
        }, 2000);
      }
    }, 100);
  }, []);

  // Add product to bulk adjustment
  const addProductToBulk = useCallback(() => {
    if (!selectedSearchProduct) return;

    const pcsInput = document.getElementById('bulk-quantity-pcs-input');
    const ctnInput = document.getElementById('bulk-quantity-ctn-input');
    
    if (pcsInput && ctnInput) {
      const pcsQuantity = parseFloat(pcsInput.value) || 0;
      const ctnQuantity = parseFloat(ctnInput.value) || 0;
      
      // Calculate total quantity according to new logic
      const piecesInCtn = selectedSearchProduct.pieces_in_ctn || 1;
      const totalPieces = pcsQuantity + (ctnQuantity * piecesInCtn);
      
      // Calculate NEW QTY in cartons (convert pieces to cartons and add to cartons)
      // Test scenarios:
      // Scenario 1: 1 carton = 10 pieces, Input: 7 pieces + 2 cartons → NEW QTY: 2.7 ctn
      // Scenario 2: 1 carton = 24 pieces, Input: 15 pieces + 1 carton → NEW QTY: 1.6 ctn  
      // Scenario 3: 1 carton = 6 pieces, Input: 8 pieces + 0 cartons → NEW QTY: 1.3 ctn
      // Scenario 4: 1 carton = 1 piece, Input: 5 pieces + 0 cartons → NEW QTY: 5 pcs
      const piecesAsCartons = piecesInCtn > 1 ? pcsQuantity / piecesInCtn : 0;
      const totalCartons = ctnQuantity + piecesAsCartons;
      
      
      
      // Check if product already exists
      const existingProductIndex = bulkAdjustmentForm.products.findIndex(p => p.id === selectedSearchProduct.id);
      
      if (existingProductIndex !== -1) {
        // Product exists - update quantities
        setBulkAdjustmentForm(prev => ({
          ...prev,
          products: prev.products.map((product, index) => {
            if (index === existingProductIndex) {
              return {
                ...product,
                raw_pcs_quantity: pcsQuantity,        // NEW PCS: Raw input from user
                raw_ctn_quantity: ctnQuantity,        // NEW CTN: Raw input from user
                total_quantity: totalPieces,          // Total pieces (for calculations)
                new_qty_display: piecesInCtn > 1 ? totalCartons.toFixed(2) + ' ctn' : totalPieces + ' pcs', // NEW QTY: Display format
                adjustment_amount: totalPieces - (product.current_stock || 0)
              };
            }
            return product;
          })
        }));
      } else {
        // Product doesn't exist - add as new entry
        const newProduct = {
          id: selectedSearchProduct.id,
          name: selectedSearchProduct.name,
          productID: selectedSearchProduct.productID,
          current_stock: selectedSearchProduct.quantity || 0,
          raw_pcs_quantity: pcsQuantity,        // NEW PCS: Raw input from user
          raw_ctn_quantity: ctnQuantity,        // NEW CTN: Raw input from user
          total_quantity: totalPieces,          // Total pieces (for calculations)
          new_qty_display: piecesInCtn > 1 ? totalCartons.toFixed(2) + ' ctn' : totalPieces + ' pcs', // NEW QTY: Display format
          unit_cost: selectedSearchProduct.cost_price || 0,
          selling_price: selectedSearchProduct.selling_price_ctn || selectedSearchProduct.selling_price || selectedSearchProduct.price || 0,
          adjustment_amount: totalPieces - (selectedSearchProduct.quantity || 0),
          image_url: selectedSearchProduct.image_url,
          pieces_in_ctn: piecesInCtn
        };
        
        setBulkAdjustmentForm(prev => ({
          ...prev,
          products: [...prev.products, newProduct]
        }));
      }
      
      // Reset search
      setSearchQuery('');
      setSelectedSearchProduct(null);
      setSearchResults([]);
      setShowProductDropdown(false);
      setSelectedIndex(-1);
      
      // Reset inputs
      pcsInput.value = '0';
      ctnInput.value = '0';
      
      // Focus back to search
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="SEARCH PRODUCTS"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.style.border = '2px solid #3b82f6';
          searchInput.style.backgroundColor = '#dbeafe';
          searchInput.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
          
          setTimeout(() => {
            searchInput.style.border = '';
            searchInput.style.backgroundColor = '';
            searchInput.style.boxShadow = '';
          }, 2000);
        }
      }, 100);
    }
  }, [selectedSearchProduct, bulkAdjustmentForm.products]);

  // Handle key navigation in search
  const handleKeyDown = useCallback((e) => {
    if (!showProductDropdown || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = selectedIndex < searchResults.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(nextIndex);
        
        // Ensure the selected item is visible in the dropdown
        setTimeout(() => {
          const dropdown = document.getElementById('search-dropdown');
          const selectedItem = dropdown?.querySelector(`[data-index="${nextIndex}"]`);
          if (selectedItem) {
            selectedItem.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }, 50);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : searchResults.length - 1;
        setSelectedIndex(prevIndex);
        
        // Ensure the selected item is visible in the dropdown
        setTimeout(() => {
          const dropdown = document.getElementById('search-dropdown');
          const selectedItem = dropdown?.querySelector(`[data-index="${prevIndex}"]`);
          if (selectedItem) {
            selectedItem.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest',
              inline: 'nearest'
            });
          }
        }, 50);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleProductSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowProductDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  }, [showProductDropdown, searchResults, selectedIndex, handleProductSelect]);

  // Handle search focus
  const handleSearchFocus = useCallback(() => {
    if (searchQuery.trim().length >= 2) {
      setShowProductDropdown(true);
    }
  }, [searchQuery]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close product dropdown when clicking outside
      if (showProductDropdown && !event.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductDropdown]);

  const handleAddProductToBulk = useCallback((product) => {
    setBulkAdjustmentForm(prev => ({
      ...prev,
      products: [...prev.products, {
        ...product,
        new_quantity: product.current_stock,
        adjustment_amount: 0
      }]
    }));
  }, []);

  const handleRemoveProductFromBulk = useCallback((productId) => {
    setBulkAdjustmentForm(prev => ({
      ...prev,
      products: prev.products.filter(p => p.id !== productId)
    }));
  }, []);

  const handleBulkProductQuantityChange = useCallback((productId, field, value) => {
    setBulkAdjustmentForm(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (p.id === productId) {
          const piecesInCtn = p.pieces_in_ctn || 1;
          let updatedProduct = { ...p, [field]: value };
          
          // Recalculate total quantity according to new logic
          if (field === 'raw_pcs_quantity' || field === 'raw_ctn_quantity') {
            const rawPcsQty = field === 'raw_pcs_quantity' ? value : (p.raw_pcs_quantity || 0);
            const rawCtnQty = field === 'raw_ctn_quantity' ? value : (p.raw_ctn_quantity || 0);
            const totalPieces = rawPcsQty + (rawCtnQty * piecesInCtn);
            
            // Calculate NEW QTY in cartons (convert pieces to cartons and add to cartons)
            // Test scenarios:
            // Scenario 1: 1 carton = 10 pieces, Input: 7 pieces + 2 cartons → NEW QTY: 2.7 ctn
            // Scenario 2: 1 carton = 24 pieces, Input: 15 pieces + 1 carton → NEW QTY: 1.6 ctn  
            // Scenario 3: 1 carton = 6 pieces, Input: 8 pieces + 0 cartons → NEW QTY: 1.3 ctn
            // Scenario 4: 1 carton = 1 piece, Input: 5 pieces + 0 cartons → NEW QTY: 5 pcs
            const piecesAsCartons = piecesInCtn > 1 ? rawPcsQty / piecesInCtn : 0;
            const totalCartons = rawCtnQty + piecesAsCartons;
            
            // Update the product with new logic
            updatedProduct = {
              ...updatedProduct,
              raw_pcs_quantity: rawPcsQty,
              raw_ctn_quantity: rawCtnQty,
              total_quantity: totalPieces,
              new_qty_display: piecesInCtn > 1 ? totalCartons.toFixed(2) + ' ctn' : totalPieces + ' pcs',
              adjustment_amount: totalPieces - (p.current_stock || 0)
            };
          }
          
          return updatedProduct;
        }
        return p;
      })
    }));
  }, []);

  // Fetch adjustment reasons
  const fetchAdjustmentReasons = useCallback(async () => {
    try {
      const response = await apiService.getAdjustmentReasons();
      setAdjustmentReasons(response.data || { supplier_related: [], internal: [] });
    } catch (error) {
      console.error('Error fetching adjustment reasons:', error);
    }
  }, []);

  // Fetch approved adjustments
  const fetchApprovedAdjustments = useCallback(async () => {
    try {
      const response = await apiService.getApprovedAdjustments();
      if (response.success) {
        setApprovedAdjustments(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching approved adjustments:', error);
    }
  }, []);

  // Fetch stock data
  const fetchStockData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch stock items, stats, categories, and adjustment reasons in parallel
      const [stockResponse, statsResponse, categoriesResponse] = await Promise.all([
        apiService.getStockItems({
          ...filters,
          page: pagination.current_page,
          per_page: pagination.per_page
        }),
        apiService.getStockStats(),
        apiService.getAllCategories()
      ]);
      
      setStockItems(stockResponse.data || []);
      setPagination(stockResponse.pagination || pagination);
      setStockStats(statsResponse);
      setCategories(categoriesResponse.categories || []);
      
    } catch (error) {
      console.error('Error fetching stock data:', error);
      toast.error('Failed to load stock data');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current_page, pagination.per_page]);

  const handleStockSubmit = useCallback(async () => {
    if (!selectedProduct || !stockForm.quantity || !selectedReasonCategory || !stockForm.reason) {
      toast.error('Please fill in all required fields including adjustment category and reason');
      return;
    }

    try {
      setStockLoading(true);
      
      const adjustmentData = {
        product_id: selectedProduct.id,
        adjustment_type: stockModalType,
        quantity: parseFloat(stockForm.quantity),
        unit_of_measure: stockForm.unit_of_measure,
        reason: stockForm.reason,
        cost_price: stockForm.cost_price ? parseFloat(stockForm.cost_price) : null,
        selling_price: stockForm.selling_price ? parseFloat(stockForm.selling_price) : null
      };

      await apiService.adjustStock(adjustmentData);
      
      toast.success(`${stockModalType === 'in' ? 'Stock In' : stockModalType === 'out' ? 'Stock Out' : 'Stock Adjustment'} completed successfully!`);
      
      // Refresh stock data
      await fetchStockData();
      
      handleCloseStockModal();
      
    } catch (error) {
      console.error('Stock adjustment error:', error);
      toast.error(error.response?.data?.message || 'Failed to process stock adjustment');
    } finally {
      setStockLoading(false);
    }
  }, [selectedProduct, stockForm, stockModalType, fetchStockData, handleCloseStockModal]);

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchStockData();
    fetchAdjustmentReasons();
    fetchApprovedAdjustments();
  }, [fetchStockData, fetchAdjustmentReasons, fetchApprovedAdjustments]);


  // Set active dropdown based on current location
  useEffect(() => {
    const activeIndex = navigationItems.findIndex(item => 
      item.children && item.children.some(child => child.active)
    );
    // Note: setActiveDropdown is now handled by Layout component
  }, [location.pathname, navigationItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const getStockStatusColor = (status) => {
    const colors = {
      in_stock: 'bg-green-100 text-green-800',
      low_stock: 'bg-orange-100 text-orange-800',
      out_of_stock: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStockStatusIcon = (status) => {
    switch (status) {
      case 'in_stock':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'low_stock':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case 'out_of_stock':
        return <X className="h-4 w-4 text-red-600" />;
      default:
        return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  return (
    <Layout>
              {/* Page Header */}
              <div className="flex items-center justify-between mt-12">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">Stock Management</h1>
                  <p className="text-gray-600">Monitor and manage your inventory levels</p>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={handleStockAdjustmentClick}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-2"
                  >
                    <Edit size={20} />
                    Stock Adjustment
                  </button>
                  <button 
                    onClick={handleApproveAdjustmentClick}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center gap-2"
                  >
                    <CheckCircle size={20} />
                    Approve Adjustment
                  </button>
                  <button 
                    onClick={handlePrintStockTakeSheet}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300 flex items-center gap-2"
                  >
                    <Printer size={20} />
                    Print Stock take Sheet
                  </button>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total Items</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : stockStats.total_items || 0}
                      </p>
                    </div>
                    <Warehouse className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">In Stock</p>
                      <p className="text-2xl font-bold text-green-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : stockStats.in_stock_items || 0}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-orange-600">Low Stock</p>
                      <p className="text-2xl font-bold text-orange-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : stockStats.low_stock_items || 0}
                      </p>
                    </div>
                    <AlertCircle className="h-8 w-8 text-orange-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Out of Stock</p>
                      <p className="text-2xl font-bold text-red-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : stockStats.out_of_stock_items || 0}
                      </p>
                    </div>
                    <X className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-600">Total Value</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {loading ? <Loader2 className="animate-spin" size={24} /> : formatCurrency(stockStats.total_value || 0)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Filters and Search */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input 
                      type="text" 
                      placeholder="Search stock items..." 
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <select 
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.main_cat}
                      </option>
                    ))}
                  </select>
                  <select 
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="all">All Status</option>
                    <option value="in_stock">In Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                  <select 
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="stock_level">Sort by Stock Level</option>
                    <option value="name">Sort by Name</option>
                    <option value="value">Sort by Value</option>
                    <option value="last_updated">Sort by Last Updated</option>
                  </select>
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 flex items-center gap-2">
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Stock Items Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Stock
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Min/Max
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Value
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <Loader2 className="animate-spin h-8 w-8 text-purple-600" />
                              <span className="ml-2 text-gray-600">Loading stock items...</span>
                            </div>
                          </td>
                        </tr>
                      ) : stockItems.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <Warehouse className="h-12 w-12 text-gray-400 mb-4" />
                              <p className="text-gray-600 text-lg font-medium">No stock items found</p>
                              <p className="text-gray-500 text-sm">Add products to start managing stock</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        stockItems.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-12 h-12 rounded-lg overflow-hidden mr-3 flex-shrink-0">
                                  {item.image_url ? (
                                    <img 
                                      src={item.image_url} 
                                      alt={item.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className={`w-full h-full bg-purple-100 rounded-lg flex items-center justify-center ${item.image_url ? 'hidden' : 'flex'}`}
                                  >
                                    <Package size={20} className="text-purple-600" />
                                  </div>
                                </div>
                                <div>
                                  <div className="text-sm font-semibold text-gray-900">{item.name}</div>
                                  <div className="text-xs text-gray-500">{item.productID} • {item.category}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className={`text-sm font-semibold ${
                                  item.current_stock <= item.min_stock ? 'text-red-600' : 
                                  item.current_stock <= item.min_stock * 2 ? 'text-orange-600' : 
                                  'text-green-600'
                                }`}>
                                  {formatQuantity(item.current_stock)}
                                </span>
                                <span className="text-xs text-gray-500 ml-1">units</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.min_stock} / {item.max_stock}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatCurrency(item.unit_cost)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatCurrency(item.total_value)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                {getStockStatusIcon(item.status)}
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStockStatusColor(item.status)}`}>
                                  {item.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(item.last_updated).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button 
                                  onClick={() => handleStockOperation(item, 'adjust')}
                                  className="group/btn relative p-2 bg-blue-100 text-blue-700 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                  title="Adjust Stock"
                                >
                                  <Edit size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Adjust
                                  </span>
                                </button>
                                <button 
                                  onClick={() => handleStockOperation(item, 'in')}
                                  className="group/btn relative p-2 bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                  title="Stock In"
                                >
                                  <ArrowUp size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Stock In
                                  </span>
                                </button>
                                <button 
                                  onClick={() => handleStockOperation(item, 'out')}
                                  className="group/btn relative p-2 bg-orange-100 text-orange-700 hover:bg-orange-200 hover:text-orange-800 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                  title="Stock Out"
                                >
                                  <ArrowDown size={18} />
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                    Stock Out
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {pagination.total > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {pagination.from} to {pagination.to} of {pagination.total} results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, current_page: Math.max(1, prev.current_page - 1) }))}
                        disabled={pagination.current_page === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-sm text-gray-700">
                        Page {pagination.current_page} of {pagination.last_page}
                      </span>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, current_page: Math.min(prev.last_page, prev.current_page + 1) }))}
                        disabled={!pagination.has_more_pages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

      {/* Stock Adjustment Modal */}
      {showStockModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">
                    {stockModalType === 'in' ? 'Stock In' : 
                     stockModalType === 'out' ? 'Stock Out' : 
                     'Adjust Stock'}
                  </h2>
                  <p className="text-white text-opacity-80 text-sm">
                    {stockModalType === 'in' ? 'Add inventory to this product' :
                     stockModalType === 'out' ? 'Remove inventory from this product' :
                     'Set exact stock quantity for this product'}
                  </p>
                </div>
                <button
                  onClick={handleCloseStockModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    {selectedProduct.image_url ? (
                      <img 
                        src={selectedProduct.image_url} 
                        alt={selectedProduct.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-full bg-purple-100 rounded-lg flex items-center justify-center ${selectedProduct.image_url ? 'hidden' : 'flex'}`}
                    >
                      <Package size={20} className="text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedProduct.name}</h3>
                    <p className="text-sm text-gray-500">{selectedProduct.productID}</p>
                    <p className="text-sm text-gray-600">Current Stock: <span className="font-medium">{formatQuantity(selectedProduct.current_stock)} {selectedProduct.unit_of_measure}</span></p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={stockForm.quantity}
                      onChange={(e) => handleStockFormChange('quantity', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Enter quantity"
                    />
                    <select
                      value={stockForm.unit_of_measure}
                      onChange={(e) => handleStockFormChange('unit_of_measure', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="pcs">Pieces</option>
                      <option value="ctn">Carton</option>
                    </select>
                  </div>
                </div>

                {/* Reason Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adjustment Category *
                  </label>
                  <select
                    value={selectedReasonCategory}
                    onChange={(e) => {
                      setSelectedReasonCategory(e.target.value);
                      handleStockFormChange('reason', ''); // Reset reason when category changes
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">-- Select Adjustment Category --</option>
                    <option value="supplier_related">Supplier Related (Financial Impact)</option>
                    <option value="internal">Internal Adjustment (No Financial Impact)</option>
                  </select>
                </div>

                {/* Reason */}
                {selectedReasonCategory && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specific Reason *
                    </label>
                    <select
                      value={stockForm.reason}
                      onChange={(e) => handleStockFormChange('reason', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">-- Select Your Reason For Adjustment --</option>
                      {adjustmentReasons[selectedReasonCategory]?.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                    
                    {/* Category Description */}
                    <div className="mt-2 p-3 rounded-lg text-sm">
                      {selectedReasonCategory === 'supplier_related' ? (
                        <div className="bg-orange-50 border border-orange-200 text-orange-800">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle size={16} />
                            <span className="font-medium">Supplier Financial Impact</span>
                          </div>
                          <p>This adjustment will deduct the monetary value from the supplier's account. Use for damaged goods, shortages, or quality issues from the supplier.</p>
                        </div>
                      ) : selectedReasonCategory === 'internal' ? (
                        <div className="bg-blue-50 border border-blue-200 text-blue-800">
                          <div className="flex items-center gap-2 mb-1">
                            <Info size={16} />
                            <span className="font-medium">Internal Adjustment</span>
                          </div>
                          <p>This adjustment only affects stock quantity. No financial impact on supplier accounts. Use for stock takes, recounts, or internal corrections.</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows="2"
                    placeholder="Add any additional details about this adjustment"
                  />
                </div>

                {/* Optional: Cost Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cost Price (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={stockForm.cost_price}
                    onChange={(e) => handleStockFormChange('cost_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter cost price per unit"
                  />
                </div>

                {/* Optional: Selling Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selling Price (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={stockForm.selling_price}
                    onChange={(e) => handleStockFormChange('selling_price', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter selling price per unit"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={handleCloseStockModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStockSubmit}
                disabled={stockLoading}
                className={`px-6 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                  stockModalType === 'in' 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : stockModalType === 'out'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } ${stockLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {stockLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {stockModalType === 'in' ? (
                      <>
                        <ArrowUp size={16} />
                        Stock In
                      </>
                    ) : stockModalType === 'out' ? (
                      <>
                        <ArrowDown size={16} />
                        Stock Out
                      </>
                    ) : (
                      <>
                        <Edit size={16} />
                        Adjust Stock
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showStockAdjustmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-red-500 to-blue-600 rounded-t-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/90 to-blue-600/90"></div>
              <div className="relative px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg border border-white/30">
                      <Edit size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 pr-8">
                          <h2 className="text-2xl font-bold text-white">Bulk Stock Adjustment</h2>
                          <p className="text-white/80 text-sm mt-1">
                            Adjust stock levels for multiple products at once
                          </p>
                        </div>
                        <div className="flex items-center space-x-3 flex-shrink-0">
                          <label className="text-white/90 text-sm font-medium whitespace-nowrap">Adjustment ID:</label>
                          <div className="bg-white/20 border border-white/30 rounded-lg px-3 py-1.5 min-w-[140px] shadow-lg">
                            <span className="text-white font-mono text-sm font-semibold">{adjustmentId || 'Generating...'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleProcessAdjustment}
                      disabled={bulkAdjustmentForm.products.length === 0 || !bulkAdjustmentForm.reason}
                      className="px-6 py-2.5 bg-white text-gray-900 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Edit size={18} />
                      Save Adjustment
                    </button>
                    <button
                      onClick={handleCloseStockAdjustmentModal}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto">
              {/* Adjustment Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adjustment Type
                  </label>
                  <select
                    value={bulkAdjustmentForm.adjustment_type}
                    onChange={(e) => handleBulkAdjustmentFormChange('adjustment_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="adjust">Set Exact Quantity</option>
                    <option value="in">Add to Stock</option>
                    <option value="out">Remove from Stock</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason Category
                  </label>
                  <select
                    value={bulkAdjustmentForm.reason_category}
                    onChange={(e) => handleBulkAdjustmentFormChange('reason_category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">-- Select Category --</option>
                    <option value="supplier_related">Supplier Related</option>
                    <option value="internal">Internal Adjustment</option>
                  </select>
                </div>

                {bulkAdjustmentForm.reason_category && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Specific Reason
                    </label>
                    <select
                      value={bulkAdjustmentForm.reason}
                      onChange={(e) => handleBulkAdjustmentFormChange('reason', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">-- Select Reason --</option>
                      {adjustmentReasons[bulkAdjustmentForm.reason_category]?.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>


              {/* Product Search and Selection */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Search Header */}
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Search Products</h3>
                    <div className="flex items-center space-x-3">
                      <button className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors">
                        All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search Input */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative max-w-md product-search-container">
                      <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown}
                        onFocus={handleSearchFocus}
                        placeholder="SEARCH PRODUCTS BY NAME AND PRODUCTID..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm uppercase"
                      />
                      <button
                        onClick={getAllProducts}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-medium bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                        title="Show all products"
                      >
                        All
                      </button>
                      {searching && (
                        <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                      
                      {/* Search Results Dropdown */}
                      {searchResults.length > 0 && showProductDropdown && !isQuantityFocused && !isUomFocused && !selectedSearchProduct && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto" id="search-dropdown">
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 font-medium">
                            {searchQuery.trim() ? `Found ${searchResults.length} products` : `Showing all ${searchResults.length} products`}
                          </div>
                          {searchResults.map((product, index) => (
                            <div
                              key={product.id}
                              data-index={index}
                              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all duration-200 ${
                                index === selectedIndex 
                                  ? 'bg-blue-500 text-white shadow-lg transform scale-[1.02] border-blue-400' 
                                  : 'hover:bg-gray-50 hover:shadow-sm'
                              }`}
                              onClick={() => handleProductSelect(product)}
                              ref={(el) => {
                                if (el && index === selectedIndex) {
                                  el.scrollIntoView({ 
                                    behavior: 'smooth', 
                                    block: 'nearest',
                                    inline: 'nearest'
                                  });
                                }
                              }}
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                                  {product.image_url ? (
                                    <img 
                                      src={product.image_url} 
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className={`w-full h-full rounded-lg flex items-center justify-center ${
                                      index === selectedIndex ? 'bg-blue-400' : 'bg-blue-100'
                                    }`}>
                                      <Package size={16} className={index === selectedIndex ? 'text-white' : 'text-blue-600'} />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${
                                    index === selectedIndex ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {product.name}
                                  </p>
                                  <p className={`text-xs ${
                                    index === selectedIndex ? 'text-blue-100' : 'text-gray-500'
                                  }`}>
                                    ₵{product.cost_price || 0}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`text-xs font-medium ${
                                    index === selectedIndex ? 'text-blue-100' : 'text-gray-600'
                                  }`}>
                                    {product.pieces_in_ctn > 1 ? `${product.pieces_in_ctn} pcs/ctn` : 'Pieces only'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap">Qty Pcs:</label>
                        <input
                          id="bulk-quantity-pcs-input"
                          type="number"
                          min="0"
                          step="0.01"
                          defaultValue="0"
                          onFocus={(e) => {
                            setIsQuantityFocused(true);
                            e.target.select(); // Highlight the text
                          }}
                          onBlur={() => setIsQuantityFocused(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const ctnInput = document.getElementById('bulk-quantity-ctn-input');
                              if (ctnInput) {
                                ctnInput.focus();
                                ctnInput.style.border = '2px solid #3b82f6';
                                ctnInput.style.backgroundColor = '#dbeafe';
                                ctnInput.style.boxShadow = '0 0 8px rgba(59, 130, 246, 0.3)';
                                
                                setTimeout(() => {
                                  ctnInput.style.border = '';
                                  ctnInput.style.backgroundColor = '';
                                  ctnInput.style.boxShadow = '';
                                }, 2000);
                              }
                            }
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap">Qty Ctn:</label>
                        <input
                          id="bulk-quantity-ctn-input"
                          type="number"
                          min="0"
                          step="0.01"
                          defaultValue="0"
                          onFocus={(e) => {
                            setIsUomFocused(true);
                            e.target.select(); // Highlight the text
                          }}
                          onBlur={() => setIsUomFocused(false)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addProductToBulk();
                            }
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap">Current:</label>
                        <input
                          type="number"
                          value={selectedSearchProduct ? formatQuantity(selectedSearchProduct.quantity || 0) : '0'}
                          readOnly
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Table - Fixed Header, Scrollable Body */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-64 lg:h-80">
                  {/* Table Header - Fixed */}
                  <div className="bg-gray-800 text-white px-6 py-4 flex font-semibold text-sm flex-shrink-0">
                    <div className="w-52 flex-shrink-0 pl-2">PRODUCT NAME</div>
                    <div className="w-24 text-right flex-shrink-0 pr-2">OLD QTY</div>
                    <div className="w-24 text-right flex-shrink-0 pr-2">NEW PCS</div>
                    <div className="w-24 text-right flex-shrink-0 pr-2">NEW CTN</div>
                    <div className="w-24 text-right flex-shrink-0 pr-2">NEW QTY</div>
                    <div className="w-28 text-right flex-shrink-0 pr-2">COST PRICE</div>
                    <div className="w-32 text-right flex-shrink-0 pr-2">SELLING PRICE</div>
                    <div className="w-28 text-right flex-shrink-0 pr-2">TOTAL COST</div>
                    <div className="w-32 text-right flex-shrink-0 pr-2">TOTAL SELLING</div>
                    <div className="w-20 text-center flex-shrink-0">ACTION</div>
                  </div>
                  
                  {/* Table Body - Scrollable */}
                  <div className="flex-1 overflow-y-auto bg-gray-50">
                    {bulkAdjustmentForm.products.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                          <Package size={24} className="text-gray-400" />
                        </div>
                        <p className="text-sm font-medium">No products selected</p>
                        <p className="text-xs text-gray-400 mt-1">Search and add products to create your bulk adjustment</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {bulkAdjustmentForm.products
                          .slice()
                          .reverse()
                          .map((product, index) => (
                          <div key={`${product.id}-${index}`} className="px-6 py-2 flex items-center border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors">
                            {/* Product Name */}
                            <div className="w-52 flex-shrink-0 pl-2">
                              <div className="text-sm font-medium text-gray-900 truncate" title={product.name}>
                                {product.name}
                              </div>
                            </div>
                            
                            {/* Old Qty */}
                            <div className="w-24 text-right text-sm text-gray-900 flex-shrink-0 pr-2">
                              {formatQuantity(product.current_stock || 0)}
                            </div>
                            
                            {/* New Pcs */}
                            <div className="w-24 text-right text-sm text-gray-900 flex-shrink-0 pr-2">
                              {formatQuantity(product.raw_pcs_quantity || 0)}
                            </div>
                            
                            {/* New Ctn */}
                            <div className="w-24 text-right text-sm text-gray-900 flex-shrink-0 pr-2">
                              {formatQuantity(product.raw_ctn_quantity || 0)}
                            </div>
                            
                            {/* New Qty - Display according to new logic */}
                            <div className="w-24 text-right text-sm font-semibold text-gray-900 flex-shrink-0 pr-2">
                              {product.new_qty_display || (() => {
                                const piecesInCtn = product.pieces_in_ctn || 1;
                                const rawPcs = product.raw_pcs_quantity || 0;
                                const rawCtn = product.raw_ctn_quantity || 0;
                                const piecesAsCartons = piecesInCtn > 1 ? rawPcs / piecesInCtn : 0;
                                const totalCartons = rawCtn + piecesAsCartons;
                                
                                
                                return piecesInCtn > 1 ? totalCartons.toFixed(2) + ' ctn' : (rawPcs + (rawCtn * piecesInCtn)) + ' pcs';
                              })()}
                            </div>
                            
                            {/* Cost Price */}
                            <div className="w-28 text-right text-sm text-gray-900 flex-shrink-0 pr-2">
                              {formatCurrency(product.unit_cost || 0)}
                            </div>
                            
                            {/* Selling Price */}
                            <div className="w-32 text-right text-sm text-gray-900 flex-shrink-0 pr-2">
                              {formatCurrency(product.selling_price || 0)}
                            </div>
                            
                            {/* Total Cost */}
                            <div className="w-28 text-right text-sm font-semibold text-gray-900 flex-shrink-0 pr-2">
                              {formatCurrency((product.total_quantity || 0) * (product.unit_cost || 0))}
                            </div>
                            
                            {/* Total Selling */}
                            <div className="w-32 text-right text-sm font-semibold text-gray-900 flex-shrink-0 pr-2">
                              {formatCurrency((product.total_quantity || 0) * (product.selling_price || 0))}
                            </div>
                            
                            {/* Action */}
                            <div className="w-20 flex items-center justify-center flex-shrink-0">
                              <button
                                onClick={() => handleRemoveProductFromBulk(product.id)}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove product"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Modal Footer - Cart Summary */}
            <div className="bg-gradient-to-r from-gray-800 to-blue-800 text-white">
              <div className="px-6 py-4">
                <div className="flex items-center justify-center space-x-8">
                  <div>
                    <span className="text-white/70">Total Items:</span>
                    <span className="ml-2 font-medium text-lg">
                      {bulkAdjustmentForm.products.length}
                    </span>
                  </div>
                  <div>
                    <span className="text-white/70">Value at Cost:</span>
                    <span className="ml-2 font-medium text-blue-300">
                      {formatCurrency(bulkAdjustmentForm.products.reduce((sum, p) => sum + ((p.total_quantity || 0) * (p.unit_cost || 0)), 0))}
                    </span>
                  </div>
                  <div>
                    <span className="text-white/70">Value at Selling:</span>
                    <span className="ml-2 font-medium text-green-300">
                      {formatCurrency(bulkAdjustmentForm.products.reduce((sum, p) => sum + ((p.total_quantity || 0) * (p.selling_price || 0)), 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Adjustment Modal */}
      {showApproveAdjustmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-red-500 to-blue-600 rounded-t-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/90 to-blue-600/90"></div>
              <div className="relative px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg border border-white/30">
                      <CheckCircle size={20} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Approve Stock Adjustments</h2>
                      <p className="text-white/80 text-xs">
                        Review and approve pending stock adjustment requests
                      </p>
                    </div>
                  </div>
                    <div className="flex items-center space-x-3">
                     <div className="flex items-center space-x-2 -ml-4">
                       <label className="text-white/90 text-sm font-semibold">Adjustment ID:</label>
                      <select
                        value={selectedBatchId}
                        onChange={(e) => setSelectedBatchId(e.target.value)}
                        className="px-2 py-1.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50 w-40"
                      >
                        <option value="">Select Adjustment ID</option>
                        {batchIds.map((batch) => (
                          <option key={batch.batch_id} value={batch.batch_id} className="text-gray-900">
                            {batch.batch_id}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button 
                      onClick={handleProcessSelectedAdjustment}
                      disabled={stockLoading || !selectedBatchId || !selectedAction}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle size={16} />
                      {stockLoading ? 'Processing...' : 'Process Adjustment'}
                      </button>
                    <div className="relative">
                      <select
                        value={selectedAction}
                        onChange={(e) => setSelectedAction(e.target.value)}
                        disabled={stockLoading}
                        className="px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-50 transition-all duration-200 text-sm font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed appearance-none pr-8"
                      >
                        <option value="">Select Action</option>
                        <option value="approve">Approve</option>
                        <option value="reject">Reject</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <button
                      onClick={handleCloseApproveAdjustmentModal}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4 max-h-[calc(90vh-180px)] overflow-y-auto">
              {(() => {
                // Show pending adjustments when an ID is selected
                if (selectedBatchId) {
                  const filteredAdjustments = pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId);
                  
                  if (filteredAdjustments.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <CheckCircle size={64} className="mx-auto text-green-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Adjustments Found</h3>
                        <p className="text-gray-500">
                          No adjustments found for the selected batch ID.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      <div className="border-b border-gray-200 pb-2">
                        <h3 className="text-lg font-medium text-gray-900">Pending Adjustments - {selectedBatchId}</h3>
                        <p className="text-sm text-gray-500">Review adjustments for approval or rejection</p>
                      </div>
                      
                      {/* Professional Table for Pending Adjustments */}
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-blue-900">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  PRODUCT NAME
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  OLD QTY
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  NEW PCS
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  NEW CTN
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  NEW QTY
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  COST PRICE
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  SELLING PRICE
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  TOTAL COST
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  TOTAL SELLING
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                  ACTION
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredAdjustments.map((adjustment, index) => (
                                <tr key={adjustment.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {adjustment.product_name}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatQuantity(adjustment.current_stock)} {adjustment.stock_unit || 'pieces'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatQuantity(adjustment.new_pcs || 0)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatQuantity(adjustment.new_ctn || 0)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {formatQuantity(adjustment.proposed_stock)} {adjustment.stock_unit || 'pieces'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {adjustment.cost_price || '₵0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {adjustment.selling_price || '₵0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {adjustment.total_cost || '₵0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {adjustment.total_selling || '₵0.00'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex items-center space-x-2">
                                      <button 
                                        onClick={() => handleApproveIndividualAdjustment(adjustment.id, 'reject')}
                                        disabled={stockLoading}
                                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                        title="Reject"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                      <button 
                                        onClick={() => handleApproveIndividualAdjustment(adjustment.id, 'approve')}
                                        disabled={stockLoading}
                                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                        title="Approve"
                                      >
                                        <Check size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Default view: Show approved adjustments table
                return (
                  <div className="space-y-4">
                    <div className="border-b border-gray-200 pb-2">
                      <h3 className="text-lg font-medium text-gray-900">Approved Adjustments</h3>
                      <p className="text-sm text-gray-500">Recently processed adjustments</p>
                    </div>
                    
                    {approvedAdjustments.length > 0 ? (
                      /* Table for Approved Adjustments */
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Batch Num
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Approved Adjustments
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Approved Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Created by
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Approved by
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {approvedAdjustments.map((approvedBatch) => (
                                <tr key={approvedBatch.batch_id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {approvedBatch.batch_id}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    Approved Adjustments of {approvedBatch.adjustments.length} item(s)
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(approvedBatch.processed_at).toLocaleDateString()} {new Date(approvedBatch.processed_at).toLocaleTimeString()}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {approvedBatch.adjustments[0]?.requested_by || 'Unknown'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    Current User
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <button 
                                      onClick={() => handleViewDetails(approvedBatch)}
                                      className="px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-xs font-medium"
                                    >
                                      View Details
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Package size={64} className="mx-auto text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Approved Adjustments</h3>
                        <p className="text-gray-500">
                          No adjustments have been approved yet. Select an Adjustment ID to review pending adjustments.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer - Approval Summary */}
            <div className="bg-gradient-to-r from-gray-800 to-blue-800 text-white">
              <div className="flex items-center justify-between px-6 py-4 text-sm">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-white/70">
                      {selectedBatchId ? 'Selected Batch:' : 'Available Batches:'}
                    </span>
                    <span className="font-medium">
                      {selectedBatchId 
                        ? pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId).length
                        : batchIds.length
                      }
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white/70">Total Value:</span>
                    <span className="font-medium">
                      {selectedBatchId ? (
                        formatCurrency(
                          pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId)
                            .reduce((sum, adj) => sum + (adj.financial_amount || 0), 0)
                        )
                      ) : (
                        'Select batch to view'
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div>
                    <span className="text-white/70">Approved:</span>
                    <span className="ml-2 font-medium text-green-400">
                      {selectedBatchId 
                        ? pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId && adj.status === 'approved').length
                        : 0
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-white/70">Rejected:</span>
                    <span className="ml-2 font-medium text-red-400">
                      {selectedBatchId 
                        ? pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId && adj.status === 'rejected').length
                        : 0
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-white/70">Status:</span>
                    <span className="ml-2 font-medium text-yellow-400">
                      {selectedBatchId 
                        ? (() => {
                            const batchAdjustments = pendingAdjustments.filter(adj => adj.batch_id === selectedBatchId);
                            if (batchAdjustments.length === 0) return 'No Data';
                            const hasApproved = batchAdjustments.some(adj => adj.status === 'approved');
                            const hasRejected = batchAdjustments.some(adj => adj.status === 'rejected');
                            const allPending = batchAdjustments.every(adj => adj.status === 'pending');
                            
                            if (hasApproved && hasRejected) return 'Mixed';
                            if (hasApproved) return 'Approved';
                            if (hasRejected) return 'Rejected';
                            if (allPending) return 'Pending Review';
                            return 'Unknown';
                          })()
                        : 'Select Batch'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedBatchDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/90 to-purple-600/90"></div>
              <div className="relative px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white">Adjustment Details</h2>
                    <p className="text-white/80 text-sm mt-1">
                      Batch ID: {selectedBatchDetails.batch_id}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseDetailsModal}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Batch Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Batch Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Total Items:</span>
                    <p className="font-medium text-gray-900">{selectedBatchDetails.adjustments.length}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Status:</span>
                    <p className={`font-medium ${selectedBatchDetails.action === 'approved' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedBatchDetails.action.charAt(0).toUpperCase() + selectedBatchDetails.action.slice(1)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Processed At:</span>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedBatchDetails.processed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Requested By:</span>
                    <p className="font-medium text-gray-900">
                      {selectedBatchDetails.adjustments[0]?.requested_by || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Adjustments Table */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Product Adjustments</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Current Stock
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Adjusted Qty
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          New Stock
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Selling Price
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedBatchDetails.adjustments.map((adjustment, index) => (
                        <tr key={adjustment.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {adjustment.product_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID: {adjustment.product_id_display}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatQuantity(adjustment.current_stock)} {adjustment.stock_unit || 'pieces'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-medium ${adjustment.adjustment_type === 'adjust' ? 'text-green-600' : 'text-red-600'}`}>
                              {adjustment.adjustment_type === 'adjust' ? '+' : '-'}{formatQuantity(adjustment.quantity_adjusted || 0)}
                            </span>
                            <span className="text-gray-500 ml-1">{adjustment.unit_of_measure}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatQuantity(adjustment.proposed_stock)} {adjustment.stock_unit || 'pieces'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              adjustment.adjustment_type === 'adjust' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {adjustment.adjustment_type === 'adjust' ? 'Increase' : 'Decrease'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {adjustment.reason || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {adjustment.cost_price || '₵0.00'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {adjustment.selling_price || '₵0.00'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Stock Take Sheet Modal */}
      {showPrintStockTakeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:bg-white print:relative">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden print:shadow-none print:max-w-full print:max-h-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 flex items-center justify-between print:hidden">
              <div className="flex items-center space-x-3">
                <Printer size={28} />
                <div>
                  <h2 className="text-2xl font-bold">Print Stock Take Sheet</h2>
                  <p className="text-purple-100 text-sm">Preview and filter before printing</p>
                </div>
              </div>
              <button
                onClick={() => setShowPrintStockTakeModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Filter Section */}
              <div className="mb-6 bg-gray-50 p-3 rounded-lg print:hidden">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Filter Stock Items</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setStockLevelFilter('all')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                      stockLevelFilter === 'all'
                        ? 'bg-gray-700 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    <div className="text-xs">All Items</div>
                    <div className="text-base font-bold">{stockItems.length}</div>
                  </button>
                  <button
                    onClick={() => setStockLevelFilter('positive')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                      stockLevelFilter === 'positive'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    <div className="text-xs">Positive Stock</div>
                    <div className="text-base font-bold">
                      {stockItems.filter(item => (item.current_stock || 0) > 0).length}
                    </div>
                  </button>
                  <button
                    onClick={() => setStockLevelFilter('zero')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                      stockLevelFilter === 'zero'
                        ? 'bg-amber-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    <div className="text-xs">Zero Stock</div>
                    <div className="text-base font-bold">
                      {stockItems.filter(item => (item.current_stock || 0) === 0).length}
                    </div>
                  </button>
                  <button
                    onClick={() => setStockLevelFilter('negative')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-all text-sm ${
                      stockLevelFilter === 'negative'
                        ? 'bg-rose-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    <div className="text-xs">Negative Stock</div>
                    <div className="text-base font-bold">
                      {stockItems.filter(item => (item.current_stock || 0) < 0).length}
                    </div>
                  </button>
                </div>
              </div>

              {/* Preview Table */}
              <div className="bg-white rounded-lg border border-gray-200 print:border-0">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 print:hidden">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Preview ({getFilteredStockItems.length} items)
                  </h3>
                </div>
                
                {/* Print-only header */}
                <div className="hidden print:block text-center mb-4">
                  <h1 className="text-2xl font-bold text-gray-900">Stock Take Sheet</h1>
                  <p className="text-sm text-gray-600">
                    {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <p className="text-sm text-gray-600 font-semibold">
                    {stockLevelFilter === 'all' ? 'All Items' : 
                     stockLevelFilter === 'positive' ? 'Positive Stock Only' :
                     stockLevelFilter === 'zero' ? 'Zero Stock Only' : 'Negative Stock Only'} 
                    ({getFilteredStockItems.length} items)
                  </p>
                </div>
                <div className="overflow-x-auto max-h-96 print:overflow-visible print:max-h-full">
                  <table className="min-w-full divide-y divide-gray-200 print:border print:border-gray-300">
                    <thead className="bg-gray-50 sticky top-0 print:static">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          Product Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          UOM
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          System Stock
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          Physical Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:border print:border-gray-300 print:bg-gray-100">
                          Variance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredStockItems.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                            No items match the selected filter
                          </td>
                        </tr>
                      ) : (
                        getFilteredStockItems.map((item, index) => (
                          <tr key={item.id || index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 print:border print:border-gray-300">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 print:border print:border-gray-300">
                              {item.name || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 print:border print:border-gray-300 print:text-gray-900">
                              {item.category || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 print:border print:border-gray-300 print:text-gray-900">
                              {item.unit_of_measure || 'pcs'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium print:border print:border-gray-300">
                              <span className={`${
                                (item.current_stock || 0) > 0 ? 'text-green-600 print:text-gray-900' :
                                (item.current_stock || 0) === 0 ? 'text-yellow-600 print:text-gray-900' :
                                'text-red-600 print:text-gray-900'
                              }`}>
                                {formatQuantity(item.current_stock || 0)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 print:border print:border-gray-300">
                              {/* Empty for physical count */}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 print:border print:border-gray-300">
                              {/* Empty for variance */}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Print-only footer */}
                <div className="hidden print:block mt-8 text-center text-sm text-gray-600">
                  <p className="mb-2">Counted by: __________________ &nbsp;&nbsp; Date: __________________ &nbsp;&nbsp; Signature: __________________</p>
                  <p>Verified by: __________________ &nbsp;&nbsp; Date: __________________ &nbsp;&nbsp; Signature: __________________</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200 print:hidden">
              <button
                onClick={() => setShowPrintStockTakeModal(false)}
                className="px-6 py-2 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePrintFromModal}
                disabled={getFilteredStockItems.length === 0}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300 flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer size={20} />
                Print Stock Take Sheet
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StockManagement;
